import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { ExtraClassesResponse } from '../types';

export const getUpcomingExtraClasses = (): Promise<ExtraClassesResponse> => {
  return axios.get('/user/v1/extra_classes/upcoming');
};

const generateZoomMeetingId = (): string => {
  return faker.number
    .int({ min: 80_000_000_000, max: 89_999_999_999 })
    .toString();
};

const generateZoomLink = (meetingId: string): string => {
  return `https://us02web.zoom.us/j/${meetingId}`;
};

function* generateHobbyClassSessions(
  classNames: string[],
  sessionType: string = 'single'
): Generator<import('../types').SessionObject> {
  for (const className of classNames) {
    const meetingId = generateZoomMeetingId();
    yield {
      class_name: className,
      config_id: faker.string.uuid(),
      joining_link: generateZoomLink(meetingId),
      session_id: faker.string.uuid(),
      session_type: sessionType,
      zoom_meeting_id: meetingId,
    };
  }
}

function generateHobbyClasses(): import('../types').ClassObject[] {
  const now = Date.now();
  const classCount = faker.number.int({ min: 2, max: 4 });
  const hobbyActivities = [
    'calligraphy',
    'hand_craft',
    'abstract_painting',
    'coffee_painting',
    'chess_fundamentals',
    'pottery',
    'origami',
    'sketching',
    'dance',
    'music',
  ];

  const results: import('../types').ClassObject[] = [];

  for (let i = 0; i < classCount; i++) {
    // Generate time offset (1-5 hours from now)
    const timeOffset = faker.number.int({ min: 3_600_000, max: 18_000_000 });
    const timestamp = now + timeOffset;
    const sessionDate = new Date(timestamp);

    // Format readable date and time
    const readableDate = sessionDate.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const readableTime = sessionDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    // Pick 1-3 random activities for this class
    const sessionCount = faker.number.int({ min: 1, max: 3 });
    const classNames = faker.helpers.arrayElements(
      hobbyActivities,
      sessionCount
    );
    const sessionType = faker.helpers.arrayElement(['single', 'series']);

    const sessions = [...generateHobbyClassSessions(classNames, sessionType)];

    results.push({
      config_id: faker.string.uuid(),
      hobby_class_name: `${readableTime} ${faker.helpers.arrayElement([
        'Creative Arts Workshop',
        'Art & Painting Session',
        'Saturday Series Sessions',
        'Weekend Activities',
        'Hobby Class',
      ])}`,
      sessions,
      timestamp,
      status: 'active',
      readable_date: readableDate,
      readable_time: readableTime,
      timezone: 'Asia/Kolkata',
      remaining_seconds: Math.floor(timeOffset / 1000),
    });
  }

  return results;
}

// Generate master classes
function generateMasterClasses(): import('../types').MasterClassObject[] {
  const now = Date.now();
  const classCount = faker.number.int({ min: 1, max: 3 });
  const masterClassSubjects = [
    'Advanced Mathematics',
    'Science & Experiments',
    'English Literature',
    'Coding Fundamentals',
    'Creative Writing',
    'Public Speaking',
  ];

  const results: import('../types').MasterClassObject[] = [];

  for (let i = 0; i < classCount; i++) {
    // Generate time offset (1-6 hours from now)
    const timeOffset = faker.number.int({ min: 3_600_000, max: 21_600_000 });
    const timestamp = now + timeOffset;
    const sessionDate = new Date(timestamp);
    const meetingId = generateZoomMeetingId();

    // Format readable date and time
    const readableDate = sessionDate.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const readableTime = sessionDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    results.push({
      session_id: faker.string.uuid(),
      master_class_name: `${faker.helpers.arrayElement(masterClassSubjects)} Masterclass`,
      joining_link: generateZoomLink(meetingId),
      timestamp,
      status: 'active',
      readable_date: readableDate,
      readable_time: readableTime,
      timezone: 'Asia/Kolkata',
      remaining_seconds: Math.floor(timeOffset / 1000),
    });
  }

  return results;
}

// ─── DEV: Toggle extra classes (hobby / master classes) ──────────────
// Extra classes fill the *second* upcoming-session card. Set this to false
// to test the single-session ("More sessions coming up soon") and no-session
// ("No upcoming sessions yet") empty states in UpcomingSessions.
const EXTRA_CLASSES_ENABLED = false;

// Generate complete extra classes response
function generateExtraClassesResponse(): ExtraClassesResponse {
  if (!EXTRA_CLASSES_ENABLED) {
    return {
      hobby_class: { classes: [], status_code: 200 },
      master_class: { classes: [], status_code: 200 },
    };
  }

  return {
    hobby_class: {
      classes: generateHobbyClasses(),
      status_code: 200,
    },
    master_class: {
      classes: generateMasterClasses(),
      status_code: 200,
    },
  };
}

export const upcomingExtraClassesMockHandler = http.get(
  `${env.API_URL}/user/v1/extra_classes/upcoming`,
  async (): Promise<HttpResponse<ExtraClassesResponse>> => {
    await networkDelay();
    return HttpResponse.json(generateExtraClassesResponse());
  }
);
